/**
 * Billing service - client-side API wrapper for /api/billing/* endpoints.
 *
 * Handles plan listing, subscription state, checkout, customer portal, cancellations,
 * coupon validation, invoice listing, and admin endpoints.
 */

import strapiAPI from './api';

const billingService = {
  // -------- Customer-facing --------

  /**
   * GET /api/billing/plans
   * Returns the full catalog of subscription plans for the upgrade selector.
   */
  async listPlans() {
    const response = await strapiAPI.get('/api/billing/plans');
    return response?.data;
  },

  /**
   * GET /api/billing/my-subscription
   * Returns the current subscription record for the authenticated company_admin.
   */
  async getMySubscription() {
    const response = await strapiAPI.get('/api/billing/my-subscription');
    return response?.data;
  },

  /**
   * GET /api/billing/employee-usage
   * Returns enforcement info: visible vs hidden employees, current plan, recommended plan.
   */
  async getEmployeeUsage() {
    const response = await strapiAPI.get('/api/billing/employee-usage');
    return response?.data;
  },

  /**
   * GET /api/billing/recommended-plan
   */
  async getRecommendedPlan() {
    const response = await strapiAPI.get('/api/billing/recommended-plan');
    return response?.data;
  },

  /**
   * POST /api/billing/checkout-session
   * Body: { planId, successUrl?, cancelUrl?, promotionCode?, allowPromotionCodes? }
   * Returns { url } - redirect the user to this URL.
   */
  async createCheckoutSession({ planId, successUrl, cancelUrl, promotionCode, allowPromotionCodes }) {
    const response = await strapiAPI.post('/api/billing/checkout-session', {
      planId,
      successUrl,
      cancelUrl,
      promotionCode,
      allowPromotionCodes,
    });
    return response?.data?.data;
  },

  /**
   * POST /api/billing/portal-session
   * Body: { returnUrl? }
   * Returns { url } - redirect user to Stripe Customer Portal.
   */
  async createPortalSession({ returnUrl } = {}) {
    const response = await strapiAPI.post('/api/billing/portal-session', { returnUrl });
    return response?.data?.data;
  },

  /**
   * POST /api/billing/cancel
   * Cancels at the end of the current period.
   */
  async cancelSubscription() {
    const response = await strapiAPI.post('/api/billing/cancel');
    return response?.data?.data;
  },

  /**
   * POST /api/billing/reactivate
   * Reverts a scheduled cancellation.
   */
  async reactivateSubscription() {
    const response = await strapiAPI.post('/api/billing/reactivate');
    return response?.data?.data;
  },

  /**
   * POST /api/billing/change-plan
   * Body: { planId }
   * Changes the plan in place (Stripe handles proration).
   */
  async changePlan({ planId }) {
    const response = await strapiAPI.post('/api/billing/change-plan', { planId });
    return response?.data?.data;
  },

  /**
   * POST /api/billing/validate-coupon
   * Body: { code, planId? }
   * Returns: { valid, reason?, preview? }
   */
  async validateCoupon({ code, planId }) {
    const response = await strapiAPI.post('/api/billing/validate-coupon', { code, planId });
    return response?.data?.data;
  },

  /**
   * GET /api/billing/invoices?limit=&offset=&status=
   */
  async listInvoices({ limit = 50, offset = 0, status } = {}) {
    const response = await strapiAPI.get('/api/billing/invoices', {
      params: { limit, offset, status },
    });
    return response?.data;
  },

  // -------- Admin (super_admin) --------
  admin: {
    async listSubscriptions({ page = 1, pageSize = 25, status, planId, search } = {}) {
      const response = await strapiAPI.get('/api/admin/subscriptions', {
        params: { page, pageSize, status, planId, search },
      });
      return response?.data;
    },

    async getSubscription(id) {
      const response = await strapiAPI.get(`/api/admin/subscriptions/${id}`);
      return response?.data?.data;
    },

    async refreshSubscription(id) {
      const response = await strapiAPI.post(`/api/admin/subscriptions/${id}/refresh`);
      return response?.data?.data;
    },

    async overrideSubscription(id, payload) {
      const response = await strapiAPI.put(`/api/admin/subscriptions/${id}/override`, payload);
      return response?.data?.data;
    },

    async cancelNow(id) {
      const response = await strapiAPI.post(`/api/admin/subscriptions/${id}/cancel-now`);
      return response?.data?.data;
    },

    /**
     * GET /api/admin/companies/:companyId/despacho
     * Estado del check "cliente de despacho" (plan interno sin cobro).
     */
    async getDespacho(companyId) {
      const response = await strapiAPI.get(`/api/admin/companies/${companyId}/despacho`);
      return response?.data?.data;
    },

    /**
     * POST /api/admin/companies/:companyId/despacho { enabled }
     * Marca/desmarca la empresa como cliente de despacho (iguala).
     */
    async setDespacho(companyId, enabled) {
      const response = await strapiAPI.post(`/api/admin/companies/${companyId}/despacho`, { enabled });
      return response?.data?.data;
    },

    async getMetrics() {
      const response = await strapiAPI.get('/api/admin/billing/metrics');
      return response?.data?.data;
    },

    async stripeStatus() {
      const response = await strapiAPI.get('/api/admin/billing/stripe-status');
      return response?.data?.data;
    },

    async syncAllPlans() {
      const response = await strapiAPI.post('/api/admin/plans/sync-stripe');
      return response?.data?.data;
    },

    async syncSinglePlan(id) {
      const response = await strapiAPI.post(`/api/admin/plans/${id}/sync-stripe`);
      return response?.data?.data;
    },

    async listCoupons({ page = 1, pageSize = 25, isActive } = {}) {
      const response = await strapiAPI.get('/api/admin/coupons', {
        params: { page, pageSize, isActive },
      });
      return response?.data;
    },

    async createCoupon(payload) {
      const response = await strapiAPI.post('/api/admin/coupons', payload);
      return response?.data?.data;
    },

    async updateCoupon(id, payload) {
      const response = await strapiAPI.put(`/api/admin/coupons/${id}`, payload);
      return response?.data?.data;
    },

    async deleteCoupon(id) {
      const response = await strapiAPI.delete(`/api/admin/coupons/${id}`);
      return response?.data?.data;
    },

    async applyCouponToSubscription(couponId, subscriptionId) {
      const response = await strapiAPI.post(
        `/api/admin/coupons/${couponId}/apply-to-subscription`,
        { subscriptionId },
      );
      return response?.data?.data;
    },
  },
};

// -------- Helpers --------

/**
 * Map a Stripe subscription status to a human-readable Spanish label + color tone.
 */
export function describeStatus(status) {
  switch (status) {
    case 'active':
      return { label: 'Activa', tone: 'success' };
    case 'trialing':
      return { label: 'En prueba', tone: 'info' };
    case 'past_due':
      return { label: 'Pago pendiente', tone: 'warning' };
    case 'canceled':
      return { label: 'Cancelada', tone: 'muted' };
    case 'unpaid':
      return { label: 'No pagada', tone: 'error' };
    case 'incomplete':
    case 'incomplete_expired':
      return { label: 'Incompleta', tone: 'warning' };
    case 'paused':
      return { label: 'Pausada', tone: 'muted' };
    case 'grace_period':
      return { label: 'Periodo de gracia', tone: 'info' };
    case 'no_subscription':
    default:
      return { label: 'Sin plan activo', tone: 'muted' };
  }
}

/**
 * Format a number as MXN currency, no decimals when round.
 */
export function formatMxn(amount) {
  if (amount == null) return '—';
  const n = Number(amount);
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
}

/**
 * Format a date string as "15 de mayo de 2026".
 */
export function formatDateLong(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

/**
 * Format an employee-tier label using min/max.
 */
export function formatTierLabel(plan) {
  if (!plan) return '';
  if (plan.max_employees == null) {
    return `Desde ${plan.min_employees} empleados`;
  }
  if (plan.min_employees === plan.max_employees) {
    return `${plan.min_employees} empleados`;
  }
  return `${plan.min_employees}–${plan.max_employees} empleados`;
}

export default billingService;
